import { useEffect, useRef, useState } from "react";
import { Input } from "@hyperhedral/ui";
import { useAuthStore } from "../../stores/auth";
import { supabase } from "../../lib/supabase";

export function Settings() {
  const {
    user,
    schoolName: storeSchoolName,
    fullName: storeFullName,
  } = useAuthStore();
  const [schoolName, setSchoolName] = useState(storeSchoolName ?? "");
  const [fullName, setFullName] = useState(storeFullName ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Password change
  const [newPassword, setNewPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    setSchoolName(storeSchoolName ?? "");
  }, [storeSchoolName]);

  useEffect(() => {
    setFullName(storeFullName ?? "");
  }, [storeFullName]);

  useEffect(() => {
    return () => {
      clearTimeout(savedTimerRef.current);
    };
  }, []);

  async function handleSaveSchool(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    setSaved(false);
    setSaveError(null);

    try {
      const trimmedFullName = fullName.trim();

      const { error: accountError } = await supabase
        .from("accounts")
        .update({
          school_name: schoolName,
          full_name: trimmedFullName || null,
        })
        .eq("id", user.id);

      if (accountError) throw accountError;

      const { error: userError } = await supabase.auth.updateUser({
        data: {
          school_name: schoolName,
          full_name: trimmedFullName,
        },
      });

      if (userError) throw userError;

      setSaved(true);
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Failed to save. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setPasswordMsg(null);

    if (newPassword.length < 8) {
      setPasswordMsg({
        type: "error",
        text: "Password must be at least 8 characters.",
      });
      return;
    }

    setPasswordLoading(true);
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    setPasswordLoading(false);

    if (error) {
      setPasswordMsg({ type: "error", text: error.message });
    } else {
      setNewPassword("");
      setPasswordMsg({ type: "success", text: "Password updated." });
    }
  }

  return (
    <div className="workspace-page p-6 sm:p-8 lg:p-10">
      <h1 className="font-display text-2xl font-semibold text-forest-950">
        Settings
      </h1>
      <p className="mt-1 text-sm text-bark-600">
        Manage your account and licence.
      </p>

      <div className="mt-10 max-w-xl space-y-10">
        {/* School info */}
        <section className="paper-card rounded-xl border border-cream-300 bg-white p-6 sm:p-8">
          <h2 className="font-display text-lg font-semibold text-forest-900">
            School information
          </h2>
          <form onSubmit={handleSaveSchool} className="mt-5 space-y-4">
            <Input
              label="Your name"
              placeholder="e.g. Julia Smith"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
            <Input
              label="School name"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              required
            />
            <Input
              label="Email address"
              type="email"
              defaultValue={user?.email ?? ""}
              readOnly
            />
            {saveError && (
              <p className="text-sm text-red-600">{saveError}</p>
            )}
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="rounded-lg bg-forest-800 px-4 py-2 text-sm font-semibold text-cream-50 transition-colors hover:bg-forest-700 disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save changes"}
              </button>
              {saved && (
                <span className="text-sm text-forest-600">Saved.</span>
              )}
            </div>
          </form>
        </section>



        {/* Change password */}
        <section className="paper-card rounded-xl border border-cream-300 bg-white p-6 sm:p-8">
          <h2 className="font-display text-lg font-semibold text-forest-900">
            Change password
          </h2>
          <form onSubmit={handleChangePassword} className="mt-5 space-y-4">
            <Input
              label="New password"
              type="password"
              placeholder="At least 8 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            {passwordMsg && (
              <p
                className={`text-sm ${passwordMsg.type === "error" ? "text-red-600" : "text-forest-600"}`}
              >
                {passwordMsg.text}
              </p>
            )}
            <button
              type="submit"
              disabled={passwordLoading}
              className="rounded-lg bg-forest-800 px-4 py-2 text-sm font-semibold text-cream-50 transition-colors hover:bg-forest-700 disabled:opacity-60"
            >
              {passwordLoading ? "Updating..." : "Update password"}
            </button>
          </form>
        </section>



        {/* Support */}
        <section className="paper-card rounded-xl border border-cream-300 bg-white p-6 sm:p-8">
          <h2 className="font-display text-lg font-semibold text-forest-900">
            Support
          </h2>
          <p className="mt-2 text-sm text-bark-600">
            Need help? Email us at{" "}
            <a
              href="mailto:support@bookburrow.uk"
              className="font-medium text-forest-600 underline decoration-forest-300 underline-offset-2"
            >
              support@bookburrow.uk
            </a>{" "}
            and we'll get back to you within one working day.
          </p>
        </section>
      </div>
    </div>
  );
}
